import * as API from '../../api/orgsAPI'
import { User } from '@/models/Users'
import { OrgData, OrgsCreate, OrgsGeneralInfo } from '@/models/Orgs'
import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { UUID } from 'crypto'
import { uploadFiles, deleteFile, uploadWebsites, refetchWebsite, updateOrgWebsite } from '@/api/docsAPI'
import { useAuth0 } from '@/utils/auth/auth0'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useState, useEffect } from 'react'

export function useCurrentUserOrgs(
  token: string,
  currentUser: User | null,
  org_uuids: UUID[],
) {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const query = useQuery({
    queryKey: ['currentOrgs', currentUser?.uuid, org_uuids?.join(',')],
    queryFn: async () => {
      if (!currentUser || !org_uuids) return []
      const freshToken = await getAccessTokenSilently()
      const orgs = await API.fetchUserOrgsById(freshToken, org_uuids)
      return orgs
    },
    enabled: !!token && !!currentUser && !!org_uuids,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes — invalidateQueries handles explicit refreshes on org switch/update
    gcTime: 5 * 60 * 1000,
    // No refetchInterval here: polling the whole list fans out one request per
    // org every few seconds, which starves the upload's SSE connection. A running
    // regen is watched by useProfileRegenPoll, scoped to the single org in view.
  })

  const safeData = Array.isArray(query.data) ? query.data : []
  const validOrgs =
    safeData.filter((org): org is OrgData => !('error' in org))
  const orgErrors =
    safeData.filter(
      (org): org is { org_uuid: UUID; error: string } => 'error' in org,
    )

  const refetch = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['currentOrgs', currentUser?.uuid],
    })
    // After invalidation, react-query will refetch. We can await the refetch if needed,
    // but for now, just returning the data from the last successful fetch or an empty array.
    // The component using this hook will re-render with new data when it's available.
    // To return the newly fetched data directly, we'd use queryClient.fetchQuery.
    // For simplicity, we'll rely on the component re-rendering.
    // The `query.data` will be updated automatically by react-query.
    // This function primarily serves to trigger the refetch.
    // We need to ensure the calling code awaits this if it immediately depends on fresh data.
    await queryClient.refetchQueries({
      queryKey: ['currentOrgs', currentUser?.uuid],
    })
    // The `query.data` should be updated now, or very soon.
    // Let's return the current state of validOrgs, which will update on next render.
    // A more robust way might be to use `fetchQuery` if immediate data is needed.
    const refreshedQuery = queryClient.getQueryData([
      'currentOrgs',
      currentUser?.uuid,
    ])
    const safeRefreshed = Array.isArray(refreshedQuery) ? refreshedQuery : []
    return safeRefreshed.filter(
      (org): org is OrgData => !('error' in org),
    )
  }

  return {
    orgData: validOrgs,
    orgErrors,
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.error?.message,
    refetch,
  }
}

// Watch a single org's profile regen while it's updating — scoped to the org the
// user is viewing so we don't fan out a request per org (which starves the
// upload's SSE connection). While `isUpdating` (pending or in_progress), poll just
// this org; when it reaches a terminal state (idle/failed), refresh the org list
// once so the new profile + status propagate everywhere. Pass isUpdating from the
// caller's current org data.
export function useProfileRegenPoll(
  currentUser: User | null,
  orgUuid: UUID | undefined,
  isUpdating: boolean,
) {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const userUuid = currentUser?.uuid
  useEffect(() => {
    if (!userUuid || !orgUuid || !isUpdating) return
    let cancelled = false
    const id = setInterval(async () => {
      try {
        const freshToken = await getAccessTokenSilently()
        const [org] = await API.fetchUserOrgsById(freshToken, [orgUuid])
        if (cancelled || !org || 'error' in org) return
        const status = org.profile_regen_status
        // Keep polling through pending → in_progress; refresh on the terminal state.
        if (status !== 'pending' && status !== 'in_progress') {
          queryClient.invalidateQueries({ queryKey: ['currentOrgs', userUuid] })
        }
      } catch {
        /* transient — retry on the next tick */
      }
    }, 4000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [userUuid, orgUuid, isUpdating, getAccessTokenSilently, queryClient])
}

export function useCurrentOrg() {
  // Use Auth0 hook for authentication data
  const { user, getAccessTokenSilently, isLoading: authLoading } = useAuth0()

  // Use state for token
  const [token, setToken] = useState<string | null>(null)

  // Access the query client to read cache data
  const queryClient = useQueryClient()

  // Effect to fetch token when component mounts
  useEffect(() => {
    if (!user) return

    const getToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently()
        setToken(accessToken)
      } catch (error) {
        console.error('Error fetching token in useCurrentOrg')
      }
    }

    getToken()
  }, [user, getAccessTokenSilently])

  // If auth is loading or no user yet, return loading state
  if (authLoading || !user) {
    return {
      currentOrg: null,
      isLoading: true,
      allOrgs: [],
    }
  }

  // Get org data from cache if available
  const userUuid = user?.sub || null
  const orgsKey = ['currentOrgs', userUuid]
  const orgsData =
    queryClient.getQueryData<(OrgData | { org_uuid: UUID; error: string })[]>(
      orgsKey,
    )

  // If no org data in cache, return loading state
  if (!Array.isArray(orgsData)) {
    return {
      currentOrg: null,
      isLoading: true,
      allOrgs: [],
    }
  }

  // Filter valid orgs (no errors)
  const validOrgs = orgsData.filter(
    (org): org is OrgData =>
      org && typeof org === 'object' && !('error' in org),
  )

  // If no valid orgs, return empty result
  if (validOrgs.length === 0) {
    return {
      currentOrg: null,
      isLoading: false,
      allOrgs: [],
    }
  }

  // Find selected org or use first org
  const selectedOrgUuid = user.selected_org_uuid || null
  let currentOrg = null

  // Try to find org by selected UUID
  if (selectedOrgUuid) {
    currentOrg = validOrgs.find((org) => org.uuid === selectedOrgUuid)
  }

  // Fallback to first org if selected not found
  if (!currentOrg && validOrgs.length > 0) {
    currentOrg = validOrgs[0]
  }

  return {
    currentOrg,
    isLoading: false,
    allOrgs: validOrgs,
  }
}

function useOrgDocsMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>,
) {
  const queryClient = useQueryClient()
  const { user: dbUser } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgDocs'] })
      // Adding/removing docs flags the profile stale server-side — refresh org
      // data so the regenerate banner reflects the new state.
      queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
      // Invalidate onboarding data to update wizard immediately
      if (dbUser?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', dbUser.uuid],
          exact: false, // This will match all variations including debug flag
        })
      }
    },
  })
}

function useOrgWebsiteMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>,
) {
  const queryClient = useQueryClient()
  const { user: dbUser } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orgDocs'] })
      // Adding a website flags the profile stale server-side — refresh org data
      // so the regenerate banner reflects the new state.
      queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
      // Invalidate onboarding data to update wizard immediately
      if (dbUser?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', dbUser.uuid],
          exact: false, // This will match all variations including debug flag
        })
      }
    },
  })
}

export function useUploadOrgDocMutation() {
  return useOrgDocsMutation(uploadFiles)
}

export function useDeleteOrgDocMutation() {
  return useOrgDocsMutation(deleteFile)
}

export function useUploadOrgWebsiteMutation() {
  return useOrgWebsiteMutation(uploadWebsites)
}

export function useOrgPreferences(
  orgUuid: string,
  token: string,
  needId?: number,
) {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const queryKey = needId != null
    ? ['orgPreferences', orgUuid, 'need', needId]
    : ['orgPreferences', orgUuid]

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchOrgPreferences(orgUuid, freshToken, needId)
    },
    enabled: !!token && !!orgUuid,
    retry: 1,
  })

  const addPreference = useMutation({
    mutationFn: async (preferenceText: string) => {
      const freshToken = await getAccessTokenSilently()
      return API.saveMatchPreferences(
        orgUuid,
        [{ preference_text: preferenceText, ...(needId != null && { need_id: needId }) }],
        freshToken,
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const updatePreference = useMutation({
    mutationFn: async ({
      preferenceId,
      preferenceText,
    }: {
      preferenceId: number
      preferenceText: string
    }) => {
      const freshToken = await getAccessTokenSilently()
      return API.updateOrgPreference(orgUuid, preferenceId, preferenceText, freshToken)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const deletePreference = useMutation({
    mutationFn: async (preferenceId: number) => {
      const freshToken = await getAccessTokenSilently()
      return API.deleteOrgPreference(orgUuid, preferenceId, freshToken)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    addPreference,
    updatePreference,
    deletePreference,
  }
}

export function useRefetchOrgWebsiteMutation() {
  return useOrgWebsiteMutation(refetchWebsite)
}

export function useUpdateOrgWebsiteMutation() {
  return useOrgWebsiteMutation(updateOrgWebsite)
}

export function useOrgDocs(orgUuid: UUID, token: string) {
  const { getAccessTokenSilently } = useAuth0()
  const query = useQuery({
    queryKey: ['orgDocs', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      const docs = await API.fetchOrgDocs(freshToken, orgUuid)
      return docs
    },
    enabled: !!token && !!orgUuid,
    retry: 1,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    uploadOrgDocs: useUploadOrgDocMutation(),
    deleteOrgDoc: useDeleteOrgDocMutation(),
    uploadOrgWebsites: useUploadOrgWebsiteMutation(),
    refetchOrgWebsite: useRefetchOrgWebsiteMutation(),
    updateOrgWebsite: useUpdateOrgWebsiteMutation(),
  }
}

export function useAddOrg() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      token,
      org,
      generalInfo,
    }: {
      token: string
      org: OrgsCreate
      generalInfo: OrgsGeneralInfo
    }) => {
      const result = await API.addOrg(token, org, generalInfo)
      if ('error' in result) {
        if (result.duplicate) {
          throw new API.DuplicateOrgNameError(result.error, result.duplicate)
        }
        throw new Error(result.error)
      }
      return result.createdOrg
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
      // Backend sets selected_org_uuid on org creation — refetch so frontend stays in sync.
      // Safe now that AuthenticatedRoute no longer depends on useOnboardingStatus,
      // so this background refetch won't cause Provider teardown.
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      // Invalidate the UserProvider org list so SelectOrg shows the new org immediately.
      queryClient.invalidateQueries({ queryKey: ['userOrgs'] })
    },
  })
}

export function useUpdateOrg() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      token,
      editedOrg,
    }: {
      token: string
      editedOrg: OrgData
    }) => {
      const result = await API.updateOrg({ token, editedOrg })
      if ('error' in result) {
        if (result.duplicate) {
          throw new API.DuplicateOrgNameError(result.error, result.duplicate)
        }
        throw new Error(result.error)
      }
      return result.editedOrg
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
    },
  })
}
